import { useEffect, useRef, useState } from 'react';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';

export function HeroSection() {
  const [sectionRef, isVisible] = useIntersectionObserver<HTMLElement>({ threshold: 0.3 });
  const prefersReduced = usePrefersReducedMotion();
  const [lineVisibility, setLineVisibility] = useState([false, false, false]);
  const hasRevealed = useRef(false);

  useEffect(() => {
    if (!isVisible || hasRevealed.current) return;
    hasRevealed.current = true;

    if (prefersReduced) {
      setLineVisibility([true, true, true]);
      return;
    }

    const delays = [1200, 3000, 4800];
    const timers = delays.map((delay, i) =>
      setTimeout(() => {
        setLineVisibility((prev) => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }, delay),
    );

    return () => timers.forEach(clearTimeout);
  }, [isVisible, prefersReduced]);

  return (
    <section id="hero" className="section hero-section" aria-label="Hero" ref={sectionRef}>
      <div className="hero-gradient"></div>
      <div className="hero-content">
        <h1 className="hero-headline" aria-label="eduksy.">
          <span className="hero-word" style={{ '--word-index': 0 } as React.CSSProperties}>eduksy.</span>
        </h1>
        <div className={`hero-typewriter fade-up ${isVisible ? 'revealed' : ''}`} style={{ '--stagger': 2 } as React.CSSProperties}>
          <p
            className="hero-typewriter-line"
            data-line="0"
            style={{ opacity: lineVisibility[0] ? 1 : 0, transition: 'opacity 0.6s ease' }}
          >
            AI is making education worse.
          </p>
          <p
            className="hero-typewriter-line"
            data-line="1"
            style={{ opacity: lineVisibility[1] ? 1 : 0, transition: 'opacity 0.6s ease' }}
          >
            We&apos;re building the antidote.
          </p>
          <p
            className="hero-typewriter-line hero-typewriter-accent"
            data-line="2"
            style={{ opacity: lineVisibility[2] ? 1 : 0, transition: 'opacity 0.6s ease' }}
          >
            Fully active learning. Google Gemini. Human connection.
          </p>
        </div>
        <div className={`hero-ctas fade-up ${isVisible ? 'revealed' : ''}`} style={{ '--stagger': 3 } as React.CSSProperties}>
          <md-filled-button href="/demo">
            Experience the Demo
            <md-icon slot="icon">play_arrow</md-icon>
          </md-filled-button>
          <md-outlined-button href="/pitch-deck/">
            View Pitch Deck
            <md-icon slot="icon">slideshow</md-icon>
          </md-outlined-button>
        </div>
      </div>
    </section>
  );
}
